<template>
  <section>
    <ProductTour v-if="productTourVisible && productOutcomeFlowEnabled" />
    <div
      class="button-container"
      data-identifier="product-onboarding-checklist-drawer-button"
    >
      <OnboardingChecklistButton
        v-if="showChecklistButton"
        @click="changeShowChecklistStatus(true)"
        :tasks-remaining="incompleteItems.length"
      />
      <PassiveHelpButton
        v-if="showPassiveHelpLightbulbButton && !isCheckListEnabled"
        @click="togglePassiveHelpPopover"
      />
    </div>
    <OnboardingChecklist
      v-if="showChecklistFlow && isCheckListEnabled && !isSmallDisplay"
      @close="changeShowChecklistStatus(false)"
    />
    <PassiveHelpPopover
      v-if="showPassiveHelpLightbulbPopover"
      @close="changePassiveHelpPanelStatus(false)"
    />
    <PassiveHelpInfoPopover
      v-if="showPassiveHelpInfoFlow"
      @close="changePassiveHelpPanelStatus(false)"
    />
  </section>
</template>
<script>
import { computed, watchEffect, ref } from 'vue-demi';
import { useCurrentAccount, useCurrentUser } from '@teamwork/use';
import ProductTour from '@sections/ProductTour';
import OnboardingChecklistButton from '@widgets/OnboardingChecklistButton';
import OnboardingChecklist from '@sections/Modals/OnboardingChecklist';
import PassiveHelpPopover from '@sections/PassiveHelp/PassiveHelpPopover';
import PassiveHelpInfoPopover from '@sections/PassiveHelp/PassiveHelpInfoPopover';
import PassiveHelpButton from '@widgets/PassiveHelpButton';
import useOnboardingChecklist from '@/views/onboarding/composables/onboardingChecklist';
import useProductTour from '@/views/onboarding/composables/productTour';
import { useFeatures } from '@/platform/composables/useFeatures';
import { useLaunchDarkly } from '@/platform/composables/useLaunchDarkly';
import usePassiveHelp from '@/views/PassiveHelp/usePassiveHelp';
import useCurrentRoute from '@/platform/composables/useCurrentRoute';
import { useLightspeedBridge } from '@/platform/composables/useLightspeedBridge';

export default {
  name: 'OnboardingAndProductTours',
  components: {
    OnboardingChecklistButton,
    OnboardingChecklist,
    ProductTour,
    PassiveHelpButton,
    PassiveHelpPopover,
    PassiveHelpInfoPopover,
  },
  setup() {
    const account = useCurrentAccount();
    const user = useCurrentUser();
    const route = useCurrentRoute();
    const isOnboardingRoute = ref(false);

    const onboardingRoutePaths = ['getting-started', 'onboarding/step'];
    const {
      projectTimeBudgetsEnabled,
      projectFinancialBudgetsEnabled,
      newSidebarNavigationEnabled,
      utilizationReportEnabled,
      workloadPlannerEnabled,
      passiveHelpEnabled,
    } = useFeatures();

    const {
      changePassiveHelpPanelStatus,
      currentRoute,
      currentPassiveHelp,
      isCurrentRoutePassiveHelpEnabled,
      isPassiveHelpPanelEnabled,
      isCurrentPassiveHelpDismissed,
    } = usePassiveHelp();

    const {
      incompleteItems,
      isChecklistDismissed,
      isSmallDisplay,
      isCheckListEnabled,
      changeShowChecklistStatus,
    } = useOnboardingChecklist();

    const { isInLightspeed } = useLightspeedBridge();

    const { activeProductTourId: productTourVisible } = useProductTour();

    // Passive Help Experiment: https://app.launchdarkly.com/default/test/features/projects-passive-help/experiments
    const { ldTrackCustomEvent } = useLaunchDarkly();

    const isAdminTrialUser = computed(() => {
      return (
        user.value.administrator &&
        user.value.inOwnerCompany &&
        // account is in free trial state
        account.value.trialDaysRemaining > 0 &&
        account.value.paymentStatus !== 'paid'
      );
    });
    const productOutcomeFlowEnabled = computed(() => {
      return (
        !isInLightspeed.value &&
        projectFinancialBudgetsEnabled.value &&
        projectTimeBudgetsEnabled.value &&
        workloadPlannerEnabled.value &&
        utilizationReportEnabled.value
      );
    });
    const showChecklistFlow = computed(() => {
      return (
        !productTourVisible.value &&
        !isChecklistDismissed.value &&
        productOutcomeFlowEnabled.value &&
        newSidebarNavigationEnabled.value &&
        isAdminTrialUser.value
      );
    });

    const showPassiveHelpLightbulbFlow = computed(() => {
      return (
        passiveHelpEnabled.value === 'lightbulb' &&
        isAdminTrialUser.value &&
        isCurrentRoutePassiveHelpEnabled.value &&
        !isCurrentPassiveHelpDismissed.value
      );
    });

    const showPassiveHelpLightbulbButton = computed(() => {
      return showPassiveHelpLightbulbFlow.value;
    });

    const showPassiveHelpLightbulbPopover = computed(() => {
      return (
        showPassiveHelpLightbulbFlow.value && isPassiveHelpPanelEnabled.value
      );
    });

    const showChecklistButton = computed(() => {
      return (
        showChecklistFlow.value &&
        !isOnboardingRoute.value &&
        !isCheckListEnabled.value &&
        !isSmallDisplay.value
      );
    });

    const showPassiveHelpInfoFlow = computed(() => {
      return (
        passiveHelpEnabled.value === 'info-icon' &&
        isAdminTrialUser.value &&
        isPassiveHelpPanelEnabled.value
      );
    });

    const showPassiveHelpInfoPopover = computed(() => {
      return showPassiveHelpInfoFlow.value && isPassiveHelpPanelEnabled.value;
    });

    function togglePassiveHelpPopover() {
      changePassiveHelpPanelStatus(!isPassiveHelpPanelEnabled.value);
      if (isPassiveHelpPanelEnabled.value) {
        ldTrackCustomEvent('passive-help-icon-clicked');
      }
    }

    watchEffect(() => {
      isOnboardingRoute.value = onboardingRoutePaths.some((path) =>
        route.value.path.includes(path),
      );
    });

    return {
      user,
      account,
      showChecklistFlow,
      showChecklistButton,
      productOutcomeFlowEnabled,
      newSidebarNavigationEnabled,
      incompleteItems,
      isChecklistDismissed,
      isSmallDisplay,
      productTourVisible,
      isCheckListEnabled,
      changeShowChecklistStatus,
      changePassiveHelpPanelStatus,
      currentRoute,
      currentPassiveHelp,
      isCurrentRoutePassiveHelpEnabled,
      passiveHelpEnabled,
      isPassiveHelpPanelEnabled,
      isCurrentPassiveHelpDismissed,
      ldTrackCustomEvent,
      showPassiveHelpLightbulbButton,
      showPassiveHelpInfoPopover,
      showPassiveHelpLightbulbPopover,
      showPassiveHelpInfoFlow,
      togglePassiveHelpPopover,
    };
  },
};
</script>
<style lang="css" scoped>
.reminder-container {
  position: fixed;
  bottom: 24px;
  right: 72px;
  z-index: 9999;
  padding: 5px;
  display: flex;
  flex-direction: column;
}
.button-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1;
  padding: 5px;
  display: flex;
  flex-direction: row-reverse;
}
.futurePassiveHelp {
  height: 100px;
  width: 100px;
  background-color: green;
  position: fixed;
  bottom: 170px;
  right: 70px;
  z-index: 9999;
}
</style>
